import React from 'react';
import classNames from 'classnames';

import Data from 'data';
import Store from 'store';
import ClassSelect from 'components/ClassSelect';
import { CopyEmbedBox, CopyNotify, copyText, skillLink } from 'components/copy';
import SkillView, { CompactSkillView } from 'components/SkillView';
import { getIconPath } from 'common';
import { ItemIcon, WithItemTooltip, WithSkillTooltip, WithTripodTooltip } from 'tooltip';
import NumberInput from 'ui/number';
import Popup from 'ui/popup';
import Tooltip, { RawTooltip } from 'ui/tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faPlusSquare, faTimes } from '@fortawesome/free-solid-svg-icons';
import { OverlayContext } from 'ui/utils/OverlayManager';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { removeBreaks } from 'utils/format';

export function levelPoints(levels, byLevel) {
  const points = Object.values(levels).reduce((sum, {level}) => sum + (Data.skillPoints[level || 1] || 0), 0);
  if (!byLevel) return points;
  let result = 10;
  while (result < 50 && Data.levelPoints[result] < points) {
    result += 1;
  }
  for (const [id, {level}] of Object.entries(levels)) {
    if (!level) continue;
    result = Math.max(result, Data.skills[id]?.learn || 0);
    if (level >= 12) result = Math.max(result, 60);
    else if (level >= 11) result = Math.max(result, 55);
  }
  return result;
}

export const skillOrder = (charClass, order) => Object.keys(Data.skills).filter(id => {
  const skill = Data.skills[id];
  return skill.class === charClass && skill.sort != null;
}).sort((a, b) => {
  if (order) {
    const ia = order.indexOf(parseInt(a)), ib = order.indexOf(parseInt(b));
    if (ia !== ib) return ia - ib;
  }
  const sa = Data.skills[a], sb = Data.skills[b];
  if (sa.sort !== sb.sort) return sa.sort - sb.sort;
  return sa.learn - sb.learn;
});

const modifySkill = (id, step, func) => (embed => {
  const oembed = embed;
  embed = {...embed};
  if (embed.steps) {
    embed.steps = embed.steps.slice();
    const data = embed.steps[step] = {...embed.steps[step]};
    if (!data[id]) return oembed;
    const res = func(data[id], ofunc => {
      for (let ostep = 0; ostep < embed.steps.length; ++ostep) {
        if (ostep === step) continue;
        if (!embed.steps[ostep][id]) continue;
        const data = embed.steps[ostep] = {...embed.steps[ostep]};
        const res = ofunc(data[id]);
        if (!res) delete data[id];
        else data[id] = res;
      }
    });
    if (!res) delete data[id];
    else data[id] = res;
  } else {
    const data = embed.data = {...embed.data};
    if (!data[id]) return oembed;
    const res = func(data[id]);
    if (!res) delete data[id];
    else data[id] = res;
  }
  return embed;
});

const tripodRows = [[1, 2, 3], [4, 5, 6], [7, 8]];

function SkillTripod({ id, step, index, pvp, allSteps }) {
  const skill = Data.skills[id];
  const data = Store.useEmbed(embed => (embed.steps?.[step] || embed.data)[id]);
  const info = skill.tripods?.[index - 1];
  const dispatch = Store.useDispatch();
  const onClick = React.useCallback(() => dispatch.setEmbed(modifySkill(id, step, (data, others) => {
    const level = data.level || 1;
    data = {...data};
    data.tripods = {...data.tripods};
    const row = tripodRows.findIndex(row => row.includes(index));
    const rowLevel = 4 + 3 * row;
    if (data.tripods[index] && level >= 4 + 3 * row) {
      delete data.tripods[index];
      const reqLevel = Math.max(1, ...Object.keys(data.tripods).map(index => {
        index = parseInt(index);
        return 4 + 3 * tripodRows.findIndex(row => row.includes(index));
      }));
      if (reqLevel < rowLevel && level >= rowLevel) {
        data.level = reqLevel;
      }
    } else {
      const value = Math.max(...tripodRows[row].map(index => data.tripods[index] || 0));
      for (const index of tripodRows[row]) delete data.tripods[index];
      data.tripods[index] = Math.min(info.levels, value || 1);
      if (level < rowLevel) {
        data.level = rowLevel;
      }
      if (allSteps && others) others(data => {
        data = {...data};
        data.tripods = {...data.tripods};
        const value = Math.max(...tripodRows[row].map(index => data.tripods[index] || 0));
        for (const index of tripodRows[row]) delete data.tripods[index];
        data.tripods[index] = Math.min(info.levels, value || 1);
        return data;
      });
    }
    return data;
  })), [dispatch, id, step, index, info, allSteps]);

  const overlays = React.useContext(OverlayContext);
  const onCopy = React.useCallback(e => {
    e.stopPropagation();
    const level = data?.tripods?.[index] || 1;
    const args = [];
    args.push(`skill=${id}`);
    args.push(`index=${index}`);
    if (level > 1) args.push(`level=${level}`);
    if (pvp) args.push('pvp');
    copyText(`[la-tripod ${args.join(' ')}]${removeBreaks(info.name)}[/la-tripod]`);
    overlays.async(<CopyNotify pos={{left: e.pageX, top: e.pageY}}/>);
  }, [overlays, id, index, info, pvp, data]);

  if (!info) return null;
  const row = tripodRows.findIndex(row => row.includes(index));
  return (
    <WithTripodTooltip skill={id} tripod={index} level={data?.tripods?.[index] || 1} pvp={pvp}>
      <div className={classNames("lap-tripod", {"lap-active": data?.tripods?.[index] && (data.level || 1) >= 4 + 3 * row})}
          style={{backgroundImage: `url(${getIconPath(info.icon)})`}} onClick={onClick}>
        <FontAwesomeIcon className="lap-copy" icon={faCopy} onClick={onCopy}/>
      </div>
    </WithTripodTooltip>
  );
}

function SkillTripodLevel({ id, step, row }) {
  const skill = Data.skills[id];
  const data = Store.useEmbed(embed => (embed.steps?.[step] || embed.data)[id]);
  const index = tripodRows[row].find(idx => data?.tripods?.[idx]);
  const info = index && skill.tripods?.[index - 1];

  const dispatch = Store.useDispatch();
  const onChange = React.useCallback(value => dispatch.setEmbed(modifySkill(id, step, data => {
    data = {...data};
    data.tripods = {...data.tripods};
    data.tripods[index] = value;
    return data;
  })), [dispatch, id, step, index]);

  if (!skill.tripods) return null;
  const enabled = index && (data.level || 1) >= 4 + 3 * row && info.levels > 1;
  return <NumberInput min={1} max={info?.levels || 1} value={data?.tripods?.[index] || 1} onChange={onChange} disabled={!enabled}/>;
}

function RuneOption({ id, onSelect }) {
  const data = Data.items[id];
  const onClick = React.useCallback(() => onSelect(id), [onSelect, id]);
  return (
    <WithItemTooltip item={id}>
      <div className={`lap-skill-rune lap-item-icon lap-item-background lap-item-${Data.itemGrades[data.grade || 0].color}`} onClick={onClick}>
        <ItemIcon item={data}/>
      </div>
    </WithItemTooltip>
  );
}

export function RunePicker({ id, onSelect }) {
  const runes = React.useMemo(() => {
    const rows = {};
    const filter = Data.skills[id]?.runes || 0;
    for (const id of Object.keys(Data.runes)) {
      const item = Data.items[id], rune = Data.runes[id];
      if (!item || item.grade >= 5) continue;
      if (rune.type && !(filter & (1 << rune.type))) continue;
      rows[item.name] = (rows[item.name] || {});
      rows[item.name][item.grade] = id;
    }
    return Object.entries(rows).sort((a, b) => a[0].localeCompare(b[0])).map(row => ({
      name: row[0],
      items: Object.entries(row[1]).sort((a, b) => parseInt(b[0]) - parseInt(a[0])).map(item => item[1]),
    }));
  }, [id]);
  const onSelectNull = React.useCallback(() => onSelect(null), [onSelect]);
  return (
    <RawTooltip>
      <ul className="lap-RunePicker lap-click">
        <li>
          <span>No Rune</span>
          <div className="lap-skill-rune lap-empty" onClick={onSelectNull}/>
        </li>
        {runes.map(row => (
          <li key={row.name}>
            <span>{row.name}</span>
            {row.items.map(id => <RuneOption key={id} id={id} onSelect={onSelect}/>)}
          </li>
        ))}
      </ul>
    </RawTooltip>
  );
}

function SkillRune({ id, step, allSteps }) {
  const [expanded, setExpanded] = React.useState(false);
  const rune = Store.useEmbed(embed => (embed.steps ? embed.steps[step] : embed.data)[id]?.rune);
  const dispatch = Store.useDispatch();
  const data = Data.items[rune];

  const onToggle = React.useCallback(e => {
    const rect = e.target.closest('.lap-skill-rune').getBoundingClientRect();
    setExpanded(e => e ? null : rect);
  }, []);
  const onClose = React.useCallback(() => setExpanded(null), []);
  const onSelect = React.useCallback(rune => {
    setExpanded(null);
    dispatch.setEmbed(modifySkill(id, step, (data, other) => {
      if (allSteps && other) other(data => ({...data, rune}));
      return {...data, rune};
    }));
  }, [dispatch, id, step, allSteps]);
  const onClear = React.useCallback(e => {
    e.preventDefault();
    dispatch.setEmbed(modifySkill(id, step, (data, other) => {
      if (allSteps && other) other(data => ({...data, rune: null}));
      return {...data, rune: null};
    }));
  }, [dispatch, id, step, allSteps]);

  return <>
    {data ? (
      <WithItemTooltip item={rune}>
        <div className={classNames(`lap-skill-rune lap-item-icon lap-item-background lap-item-${Data.itemGrades[data.grade || 0].color} lap-click`, {"lap-expanded": expanded})} onClick={onToggle} onContextMenu={onClear}>
          <ItemIcon item={data}/>
        </div>
      </WithItemTooltip>
    ) : (
      <div className={classNames("lap-skill-rune lap-empty lap-click", {"lap-expanded": expanded})} onClick={onToggle}/>
    )}
    {!!expanded && (
      <Popup alignPosition={expanded} onClose={onClose}>
        <RunePicker id={id} onSelect={onSelect}/>
      </Popup>
    )}
  </>;
}

function SkillLink({ data }) {
  const text = skillLink(data);
  const overlays = React.useContext(OverlayContext);
  const onCopy = React.useCallback(e => {
    e.stopPropagation();
    copyText(text);
    overlays.async(<CopyNotify pos={{left: e.pageX, top: e.pageY}}/>);
  }, [overlays, text]);
  if (!text) return null;
  return (
    <FontAwesomeIcon className="lap-copy lap-click" icon={faCopy} onClick={onCopy}/>
  );
}

function SkillBox({ id, step, allSteps }) {
  const skill = Data.skills[id];
  const pvp = Store.useEmbed(embed => !!embed.pvp);
  const data = Store.useEmbed(embed => (embed.steps ? embed.steps[step] : embed.data)[id]);
  const dispatch = Store.useDispatch();

  const level = React.useMemo(() => {
    const tabs = Data.skillTabs[Data.skills[id].class];
    const category = Data.skills[id].category;
    const tab = tabs?.find(tab => tab.types.includes(category));
    if (tab && tab.limit === 0) return 1;
    return data?.level || 0;
  }, [data, id]);

  const onClick = React.useCallback(e => {
    if (e.target.closest('.lap-click')) return;
    dispatch.setEmbed(embed => {
      const skill = Data.skills[id];

      const tabs = Data.skillTabs[Data.skills[id].class];
      const category = Data.skills[id].category;
      const tab = tabs && tabs.find(tab => tab.types.includes(category));
      if (tab && tab.limit === 1) return embed;

      embed = {...embed};
      let data;
      if (embed.steps) {
        embed.steps = embed.steps.slice();
        data = embed.steps[step] = {...embed.steps[step]};
      } else {
        data = embed.data = {...embed.data};
      }
      if (data[id]?.level) {
        data[id] = {...data[id]};
        delete data[id].level;
      } else if (skill.awakening) {
        const other = Object.keys(data).filter(id => Data.skills[id].awakening);
        for (const oid of other) delete data[oid];
        data[id] = {level: 1};
      } else {
        const other = Object.keys(data).filter(id => {
          const skill = Data.skills[id];
          return data[id].level && !skill.awakening && (!tab || tab.types.includes(skill.category));
        });
        if (other.length < (tab?.limit || 8)) {
          data[id] = {...data[id], level: 1};
        }
      }
      return embed;
    });
  }, [dispatch, id, step]);

  const onSetLevel = React.useCallback(value => dispatch.setEmbed(embed => {
    embed = {...embed};
    let data;
    if (embed.steps) {
      embed.steps = embed.steps.slice();
      data = embed.steps[step] = {...embed.steps[step]};
    } else {
      data = embed.data = {...embed.data};
    }
    data[id] = {...data[id], level: value};
    return embed;
  }), [dispatch, id, step]);

  const skillData = {id, ...data, level};
  return (
    <WithSkillTooltip skill={skillData} align="left" pvp={pvp}>
      <li className={classNames("lap-SkillBox", {"lap-disabled": !level})} onClick={onClick}>
        <div className="lap-skill-icon" style={{backgroundImage: `url(${getIconPath(skill.icon)}`}}/>
        <div className="lap-skill-name">
          {skill.name}
          <SkillLink data={skillData}/>
        </div>
        {!!(level && skill.maxLevel > 1) && (
          <div className="lap-skill-level">
            <NumberInput className="lap-click" value={level} onChange={onSetLevel} min={1} max={12}/>
          </div>
        )}
        {!!(level && skill.tripods) && (
          <div className="lap-skill-tripods lap-click">
            {tripodRows.map((indices, row) => (
              <div className="lap-tripod-row" key={row}>
                <div className="lap-tripod-list">
                  {indices.map(index => <SkillTripod id={id} step={step} index={index} key={index} pvp={pvp} allSteps={allSteps}/>)}
                </div>
                <div className="lap-tripod-level">
                  <SkillTripodLevel id={id} step={step} row={row} allSteps={allSteps}/>
                </div>
              </div>
            ))}
          </div>
        )}
        {!!(level && !skill.awakening) && <SkillRune id={id} step={step} allSteps={allSteps}/>}
      </li>
    </WithSkillTooltip>
  );
}

function HistoryStep({ index, active, setActive, getName }) {
  const embed = Store.useEmbed();
  const name = React.useMemo(() => getName(embed.steps?.[index] || embed.data), [getName, embed, index]);
  const dispatch = Store.useDispatch();

  const onClick = React.useCallback(e => setActive(index), [index, setActive]);
  
  const onDelete = React.useCallback(e => {
    e.stopPropagation();
    const count = embed.steps?.length || 1;
    if (index === active && index >= count - 1 && count > 1) {
      setActive(index - 1);
    } else if (index < active) {
      setActive(active - 1);
    }
    dispatch.setEmbed(embed => {
      if (!embed.steps || !embed.steps.length) return embed;
      embed = {...embed};
      if (embed.steps.length === 1) {
        embed.data = embed.steps[0];
        delete embed.steps;
      } else {
        embed.steps = embed.steps.slice();
        embed.steps.splice(index, 1);
      }
      return embed;
    });
  }, [dispatch, embed, index, setActive, active]);

  return (
    <li className={classNames({"dip-active": active === index || !embed.steps})} onClick={onClick}>
      {name}
      {!!embed.steps && <FontAwesomeIcon className="dip-delete" icon={faTimes} onClick={onDelete}/>}
    </li>
  );
}

export default function SkillEditor() {
  const [step1, setStep] = React.useState(0);
  const [allSteps, setAllSteps] = React.useState(false);

  const embed = Store.useEmbed();
  const dispatch = Store.useDispatch();

  const step = embed.steps ? (embed.steps[step1] ? step1 : 0) : 0;

  const onChangeClass = React.useCallback(value => dispatch.setEmbed(embed => {
    if (embed.class === value) return embed;
    return {...embed, class: value, data: {}, steps: null};
  }), [dispatch]);

  const ids = React.useMemo(() => Object.keys(Data.skills).filter(id => {
    const skill = Data.skills[id];
    return skill.class === embed.class && skill.sort != null;
  }).sort((a, b) => {
    const sa = Data.skills[a], sb = Data.skills[b];
    if (sa.sort !== sb.sort) return sa.sort - sb.sort;
    return sa.learn - sb.learn;
  }), [embed.class]);

  const onAddStep = React.useCallback(() => {
    dispatch.setEmbed(embed => {
      embed = {...embed};
      if (!embed.steps) {
        embed.steps = [embed.data];
      }
      const pos = Math.min(embed.steps.length, step + 1);
      embed.steps.splice(pos, 0, embed.steps[pos - 1]);
      return embed;
    });
    setStep(step + 1);
  }, [dispatch, embed, step]);

  const onSwap = React.useCallback((a, b) => dispatch.setEmbed(embed => {
    embed = {...embed};
    const ids = skillOrder(embed.class, embed.order);
    const data = embed.steps ? embed.steps[embed.steps.length - 1] : embed.data;
    embed.order = ids.filter(id => !Data.skills[id]?.awakening && data[id]?.level).map(x => parseInt(x));
    a = parseInt(a);
    b = parseInt(b);
    const ia = embed.order.indexOf(a), ib = embed.order.indexOf(b);
    if (ia >= 0 && ib >= 0) {
      embed.order[ia] = b;
      embed.order[ib] = a;
    }
    return embed;
  }), [dispatch]);

  const getStepName = React.useCallback(step => {
    if (embed.level) return `Level: ${levelPoints(step, embed.level)}`;
    else return `Points: ${levelPoints(step, embed.level)}`;
  }, [embed.level]);

  const onSetLevel = React.useCallback(e => dispatch.setEmbed(embed => ({...embed, level: !!e.target.checked})), [dispatch]);
  const onSetPvp = React.useCallback(e => dispatch.setEmbed(embed => ({...embed, pvp: !!e.target.checked})), [dispatch]);
  const onSetAllSteps = React.useCallback(e => setAllSteps(!!e.target.checked), [dispatch]);

  const tabs = React.useMemo(() => Data.skillTabs[embed.class] || [{types: [...new Set(ids.map(id => Data.skills[id].category))], limit: 8}], [embed.class, ids]);

  return (
    <div className="lap-SkillEditor">
      <div className="lap-config">
        <ClassSelect value={embed.class} onChange={onChangeClass}/>
        <p><label><input type="checkbox" value={!!embed.level} onChange={onSetLevel}/> Show level instead of points</label></p>
        <p><label><input type="checkbox" value={!!embed.pvp} onChange={onSetPvp}/> Show PvP tooltips</label></p>
      </div>
      <div className="lap-editor">
        <div>
          <ul className="lap-steps">
            {!!embed.steps && embed.steps.map((_, index) => (
              <HistoryStep key={index} index={index} active={step} setActive={setStep} getName={getStepName}/>
            ))}
            {!embed.steps && <HistoryStep index={0} active={step} setActive={setStep} getName={getStepName}/>}
            <li onClick={onAddStep}>Add Step</li>
          </ul>
          <SkillView embed={embed} step={step} setStep={setStep}/>
          <CopyEmbedBox type="skills"/>
          <DndProvider backend={HTML5Backend}>
            <CompactSkillView embed={embed} onDrag={onSwap}/>
          </DndProvider>
          <CopyEmbedBox type="compactskills"/>
        </div>
        <div>
          {!!(embed.steps && embed.steps.length > 1) && (
            <p><label><input type="checkbox" value={!!allSteps} onChange={onSetAllSteps}/> Set runes and tripods for all steps</label></p>
          )}
          {tabs.map(({types, name}, index) => <React.Fragment key={index}>
            {!!name && <h3>{name}</h3>}
            <ul>
              {ids.filter(id => types.includes(Data.skills[id].category) && !Data.skills[id].awakening).map(id => <SkillBox key={id} id={id} step={step} allSteps={allSteps}/>)}
            </ul>
          </React.Fragment>)}
          <h3>Awakening</h3>
          <ul>
            {ids.filter(id => Data.skills[id].awakening).map(id => <SkillBox key={id} id={id} step={step}/>)}
          </ul>
        </div>
      </div>
    </div>
  );
}
